<template>
  <b-container fluid>
    <b-row v-if="!isOwner && !$apollo.queries.company._loading">
      <b-col>
        <AccessDenied />
      </b-col>
    </b-row>
    <div v-else>
      <FormulateForm
        name="updateCompany"
        @submit="submitHandler"
        #default="{ isLoading }"
        v-model="formValues"
        v-if="isOwner"
      >
        <b-row>
          <b-col lg="6" offset-lg="3">
            <iq-card>
              <template v-slot:headerTitle>
                <h4 class="card-title">Edit company</h4>
              </template>
              <template v-slot:body>
                <b-form-row>
                  <b-col>
                    <b-form-group label="Company name" label-for="name">
                      <FormulateInput
                        id="name"
                        name="name"
                        type="text"
                        validation="required"
                        input-class="form-control"
                      />
                    </b-form-group>
                    <b-form-group label="Identification number" label-for="in">
                      <FormulateInput id="in" name="in" type="text" input-class="form-control" />
                    </b-form-group>
                    <b-form-group label="Primary contact" label-for="primary_contact_id">
                      <FormulateInput
                        name="primaryContactId"
                        type="select"
                        placeholder="Choose contact"
                        :options="
                          contacts.map((c) => ({
                            value: c.id,
                            label: c.label,
                          }))
                        "
                      />
                    </b-form-group>
                    <b-form-group label="Business potential" label-for="businessPotential">
                      <FormulateInput
                        id="businessPotential"
                        name="businessPotential"
                        type="number"
                        input-class="form-control"
                        step="0.01"
                      />
                    </b-form-group>
                    <b-form-group label="Business potential use" label-for="businessPotentialUse">
                      <FormulateInput
                        id="businessPotentialUse"
                        name="businessPotentialUse"
                        type="number"
                        input-class="form-control"
                        step="0.01"
                      />
                    </b-form-group>
                    <b-form-group label="Number of used products" label-for="numberOfUsedProducts">
                      <FormulateInput
                        id="numberOfUsedProducts"
                        name="numberOfUsedProducts"
                        type="number"
                        step="1"
                        input-class="form-control"
                      />
                    </b-form-group>
                    <b-form-group label="Current monthly billing" label-for="currentBillingMonthly">
                      <FormulateInput
                        id="currentBillingMonthly"
                        name="currentBillingMonthly"
                        type="number"
                        step="0.01"
                        input-class="form-control"
                      />
                    </b-form-group>
                  </b-col>
                </b-form-row>
              </template>
            </iq-card>
          </b-col>
          <b-col lg="2" offset-lg="3">
            <router-link to="/companies">
              <b-button block variant="outline-primary" class="mb-3 mr-1">Back to companies</b-button>
            </router-link>
          </b-col>
          <b-col lg="4">
            <b-button
              block
              type="submit"
              variant="primary"
              class="mb-3 mr-1"
              :disabled="isLoading"
              :label="isLoading ? 'Loading...' : 'Submit this form'"
            >Save company</b-button>
          </b-col>
        </b-row>
      </FormulateForm>
    </div>
  </b-container>
</template>

<script>
import { core } from "../../config/pluginInit";
import gql from "graphql-tag";
import { composedContact } from "../../Utils/composedContact";
import { getAccountSync } from "../../Utils/account";
import AccessDenied from './Components/AccessDenied.vue';

const GET_COMPANY = gql`
  query getCompany($id: uuid!) {
    companies_by_pk(id: $id) {
      id
      in
      name
      primary_contact_id
      business_potential
      business_potential_use
      number_of_used_products
      current_billing_monthly
      owner_id
    }
  }
`;
const UPDATE_COMPANY = gql`
  mutation updateCompany(
    $id: uuid!
    $name: String!
    $in: String
    $primaryContactId: uuid
    $businessPotential: float8
    $businessPotentialUse: float8
    $numberOfUsedProducts: Int
    $currentBillingMonthly: float8
  ) {
    update_companies_by_pk(
      pk_columns: { id: $id }
      _set: {
        name: $name
        in: $in
        primary_contact_id: $primaryContactId
        business_potential: $businessPotential
        business_potential_use: $businessPotentialUse
        number_of_used_products: $numberOfUsedProducts
        current_billing_monthly: $currentBillingMonthly
      }
    ) {
      id
      in
      primary_contact_id
      business_potential
      business_potential_use
      number_of_used_products
      current_billing_monthly
    }
  }
`;
const LIST_CONTACTS = gql`
  query listContacts {
    contacts {
      id
      email
      first_name
      last_name
    }
  }
`;

export default {
  name: "CompaniesEdit",
  components: {
    AccessDenied
  },
  data() {
    return {
      formValues: {
        name: "",
        in: "",
        primaryContactId: "",
        businessPotential: "",
        businessPotentialUse: "",
        numberOfUsedProducts: "",
        currentBillingMonthly: ""
      },
      contacts: [],
      account: getAccountSync()
    };
  },
  apollo: {
    company() {
      return {
        query: GET_COMPANY,
        variables: {
          id: this.$route.params.id
        },
        notifyOnNetworkStatusChange: true,
        update: data => {
          this.formValues.name = data.companies_by_pk.name;
          this.formValues.in = data.companies_by_pk.in;
          this.formValues.primaryContactId =
            data.companies_by_pk.primary_contact_id;
          this.formValues.businessPotential =
            data.companies_by_pk.business_potential;
          this.formValues.businessPotentialUse =
            data.companies_by_pk.business_potential_use;
          this.formValues.numberOfUsedProducts =
            data.companies_by_pk.number_of_used_products;
          this.formValues.currentBillingMonthly =
            data.companies_by_pk.current_billing_monthly;

          return data.companies_by_pk;
        }
      };
    },
    contacts() {
      return {
        query: LIST_CONTACTS,
        update: data => {
          return data.contacts.map(c => ({
            id: c.id,
            label: composedContact({
              email: c.email,
              firstName: c.first_name,
              lastName: c.last_name
            })
          }));
        }
      };
    }
  },
  methods: {
    async submitHandler() {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY,
          variables: {
            id: this.$route.params.id,
            name: this.formValues.name,
            in: this.formValues.in,
            primaryContactId: this.formValues.primaryContactId,
            businessPotential: parseFloat(this.formValues.businessPotential),
            businessPotentialUse: parseFloat(
              this.formValues.businessPotentialUse
            ),
            numberOfUsedProducts: parseInt(
              this.formValues.numberOfUsedProducts
            ),
            currentBillingMonthly: parseFloat(
              this.formValues.currentBillingMonthly
            )
          }
        });
        this.$toast.success(`Company updated`);
      } catch (error) {
        this.$toast.error(`Failed to update company: ${error.message}`);
      }
    }
  },
  computed: {
    isOwner(){
      if (this?.company?.owner_id === this?.account?.userId) {
        return true
      }
      return false
    }
  },
  mounted() {
    core.index();
  }
};
</script>
